/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useEffect } from 'react';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import RecommendationsModalContent from 'src/components/profile/recommendations/RecommendationsModalContent';
import RecommendationConfirmation from 'src/components/profile/recommendations/RecommendationConfirmation';
import AddContactBlock from 'src/components/profile/AddContactBlock';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import IconButton from 'src/components/ui/IconButton';
import Button from 'src/components/ui/Button';

import useAppRouter from 'src/hooks/useAppRouter';
import useAlerts from 'src/hooks/useAlerts';

import { useRemoveRecommendationMutation } from 'src/hooks/__generated__/queries';
import type { CompanyProfileQuery, Recommendation, UserProfileQuery } from 'src/types/__generated__/graphql';
import { ContactStatus, RecommendationStatus } from 'src/types/__generated__/graphql';
import RecommendationsModalCompanyContent from 'src/components/profile/recommendations/RecommendationsModalCompanyContent';
import { colours } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 40rem;
        background-color: ${colours.grey.G100};
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
        display: flex;
        flex-direction: column;
    }
    .MuiDialogActions-root {
        padding: 0 0 1.5rem 0;
        &.MuiDialogActions-spacing > :not(:first-of-type) {
            margin-left: 1rem;
        }
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0 1.5rem;
    }
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};

export type ViewTypes =
    | 'recommendationsList'
    | 'companyRecommendations'
    | 'recommendedCoworkers'
    | 'recommendedGroupCompanies'
    | 'recommendConfirmation'
    | 'addContact'
    | 'removeRecommendationConfirmation'
    | 'searchProfile';

export type RecommendationModalOpenedType = 'fromRecommendations' | 'fromRatings' | null;

type ViewTypeTitles = Record<ViewTypes, string>;

interface Props {
    user?: NonNullable<UserProfileQuery['userProfile']>;
    company?: NonNullable<CompanyProfileQuery['companyProfile']>;
    recommendationsCount: number;
    recommendationFromMe?: Pick<Recommendation, 'id' | 'status'> | null;
    modalOpenedType: RecommendationModalOpenedType;
    setModalType: (type: RecommendationModalOpenedType) => void;
    selectedView: ViewTypes;
    setSelectedView: (view: ViewTypes) => void;
}

const RecommendationsModal: React.FC<Props> = ({
    user,
    company,
    recommendationsCount,
    modalOpenedType,
    setModalType,
    recommendationFromMe,
    selectedView,
    setSelectedView,
}) => {
    const { t } = useTranslation(['profile', 'common']);
    const router = useAppRouter();
    const { addAlert } = useAlerts();
    const [removeRecommendation] = useRemoveRecommendationMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const titles: ViewTypeTitles = {
        recommendationsList: t('profile:recommendations'),
        companyRecommendations: t('profile:recommendations'),
        recommendedCoworkers: t('profile:recommendations'),
        recommendedGroupCompanies: t('profile:recommendations'),
        recommendConfirmation: t('profile:recommend'),
        addContact: t('profile:sendInvite'),
        removeRecommendationConfirmation: t('profile:removingRecommendation'),
        searchProfile: t('common:searchProfile'),
    };

    const closeModal = () => {
        setModalType(null);
        setSelectedView('recommendationsList');
    };

    // Close modal on route changes
    useEffect(() => {
        const handleRouteChange = () => {
            closeModal();
        };
        router?.events?.on('routeChangeComplete', handleRouteChange);
        return () => {
            router?.events?.off('routeChangeComplete', handleRouteChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router?.events]);

    const handleRemoveRecommendation = async () => {
        try {
            const refetchQueries = ['RecommendingUsers'];
            if (user) {
                refetchQueries.push('UserProfile');
            } else if (company) {
                refetchQueries.push('CompanyProfile');
            }
            await removeRecommendation({
                variables: {
                    userId: user?.id,
                    companyId: company?.id,
                },
                refetchQueries,
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('profile:removeRecommendationSuccess'),
            });
            setSelectedView('recommendationsList');
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String((error as Error).message),
            });
        }
    };
    const isAlreadyRecommended = recommendationFromMe?.status === RecommendationStatus.Accepted;
    const companyIsGroup = company?.capabilities?.isAbleToHaveEstateCompanies;

    if (!user && !company) {
        throw Error('RecommendationsModal needs a user or a company');
    }

    const baseHandleNext = () => {
        setSelectedView(isAlreadyRecommended ? 'removeRecommendationConfirmation' : 'recommendConfirmation');
        if (!isAlreadyRecommended) {
            logEventDynamic(AnalyticsEvent.Recommend);
        }
    };

    return (
        <StyledDialog open={Boolean(modalOpenedType)} onClose={closeModal} fullscreenOnMobile>
            <DialogTitle onClose={closeModal} component="div">
                <Box
                    width="100%"
                    display="flex"
                    justifyContent={selectedView === 'recommendationsList' ? 'space-between' : 'start'}
                    mr="1.5rem"
                    alignItems="center"
                >
                    {[
                        'companyRecommendations',
                        'recommendedCoworkers',
                        'recommendedGroupCompanies',
                        'searchProfile',
                    ].includes(selectedView) && (
                        <Box mr="1rem">
                            <Icon name="ArrowBack" onClick={() => setSelectedView('recommendationsList')} />
                        </Box>
                    )}
                    <Typography variant="h2">{titles[selectedView]}</Typography>
                    {selectedView === 'recommendationsList' && user && (
                        <IconButton
                            onClick={() => setSelectedView('searchProfile')}
                            color="white"
                            style={{
                                backgroundColor: 'transparent',
                            }}
                            iconProps={{
                                name: 'Search',
                            }}
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                {[
                    'recommendationsList',
                    'companyRecommendations',
                    'recommendedCoworkers',
                    'recommendedGroupCompanies',
                    'searchProfile',
                ].includes(selectedView) &&
                    (user ? (
                        <RecommendationsModalContent
                            user={user}
                            company={company}
                            recommendationsCount={recommendationsCount}
                            recommendationFromMe={recommendationFromMe}
                            handleNext={baseHandleNext} // todo rename
                            type={selectedView === 'recommendationsList' ? 'list' : 'search'}
                        />
                    ) : (
                        <RecommendationsModalCompanyContent
                            company={company as NonNullable<Props['company']>}
                            viewType={selectedView}
                            handleNext={baseHandleNext}
                            recommendationFromMe={recommendationFromMe}
                            onSeeAllCompanyRecommendations={() => setSelectedView('companyRecommendations')}
                            onSeeAllRecommendedCoworkers={() => setSelectedView('recommendedCoworkers')}
                            onSeeAllRecommendedGroupCompanies={() => setSelectedView('recommendedGroupCompanies')}
                        />
                    ))}
                {selectedView === 'recommendConfirmation' && (
                    <RecommendationConfirmation
                        user={user}
                        company={company}
                        modalOpenedType={modalOpenedType}
                        handleBack={() => {
                            if (modalOpenedType === 'fromRatings') {
                                closeModal();
                            } else {
                                setSelectedView('recommendationsList');
                            }
                        }}
                        handleNext={() => {
                            const canAddContact =
                                !user?.contactStatusV2 ||
                                [ContactStatus.Uninvited, ContactStatus.ToRespond].includes(user?.contactStatusV2);
                            if (canAddContact && !company) {
                                setSelectedView('addContact');
                            } else {
                                if (modalOpenedType === 'fromRatings') {
                                    closeModal();
                                } else {
                                    setSelectedView('recommendationsList');
                                }
                            }
                        }}
                    />
                )}
                {selectedView === 'removeRecommendationConfirmation' && (
                    <Box mb="1.5rem" display="flex" flexDirection="column">
                        <Box my="1.5rem">
                            <Typography
                                variant="body1"
                                dangerouslySetInnerHTML={{
                                    __html: user
                                        ? t('profile:removeUserRecommendationConfirmText', {
                                              fullName: user.fullName,
                                          })
                                        : t(
                                              `profile:${
                                                  companyIsGroup
                                                      ? 'removeGroupRecommendationConfirmText'
                                                      : 'removeCompanyRecommendationConfirmText'
                                              }`,
                                              {
                                                  companyName: company?.name,
                                              },
                                          ),
                                }}
                            />
                        </Box>
                        <Box display="flex" justifyContent="end">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    if (modalOpenedType === 'fromRatings') {
                                        closeModal();
                                    } else {
                                        setSelectedView('recommendationsList');
                                    }
                                }}
                            >
                                {t('common:modal.cancel')}
                            </Button>
                            <Box ml="1rem">
                                <Button onClick={handleRemoveRecommendation}>{t('common:modal.confirm')}</Button>
                            </Box>
                        </Box>
                    </Box>
                )}
                {selectedView === 'addContact' && user && (
                    <AddContactBlock
                        user={user}
                        handleBack={() => {
                            if (modalOpenedType === 'fromRatings') {
                                closeModal();
                            } else {
                                setSelectedView('recommendationsList');
                            }
                        }}
                        handleNext={() => {
                            if (modalOpenedType === 'fromRatings') {
                                closeModal();
                            } else {
                                setSelectedView('recommendationsList');
                            }
                        }}
                    />
                )}
            </DialogContent>
        </StyledDialog>
    );
};

export default RecommendationsModal;
